import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BookingForm from '../components/BookingForm';
import SeoHead from '../components/SeoHead';
import {
  VEHICLES,
  DRIVER_BATA,
  MIN_ONEWAY_KM,
  getVehicleDisplayName,
} from '../data/bookingConfig';
import FeaturedRoutePromotion from '../components/FeaturedRoutePromotion';
import { getHubRelatedLinks } from '../data/hubLanding';
import '../styles/RouteLanding.css';

const BOOKING_ID = 'hub-booking';

const scrollToBooking = () => {
  const target = document.getElementById(BOOKING_ID);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const HubLandingPage = ({ page }) => {
  const [preferredVehicle, setPreferredVehicle] = useState('');
  const [selectedDrop, setSelectedDrop] = useState('');
  const [pickupOverride, setPickupOverride] = useState('');
  const relatedLinks = useMemo(() => getHubRelatedLinks(page), [page]);
  const linkedRoutes = page.priorityRoutes.filter((route) => route.path);

  const jsonLd = useMemo(() => {
    const blocks = [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://easydroptaxi.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: page.title,
            item: `https://easydroptaxi.com${page.slug}`,
          },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: page.title,
        serviceType: 'One-way taxi booking',
        provider: {
          '@type': 'TaxiService',
          name: 'Easy Drop Taxi',
          url: 'https://easydroptaxi.com/',
          telephone: '+919629954544',
        },
        areaServed: [page.name, 'Tamil Nadu'],
        url: `https://easydroptaxi.com${page.slug}`,
      },
    ];

    if (page.faqs?.length) {
      blocks.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: page.faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      });
    }

    return blocks;
  }, [page]);

  const handleSelectVehicle = (vehicleType) => {
    setPreferredVehicle(vehicleType);
    scrollToBooking();
  };

  const handleBookRoute = (destination) => {
    setSelectedDrop(destination);
    scrollToBooking();
  };

  return (
    <main className="route-landing">
      <SeoHead
        title={page.seoTitle}
        description={page.seoDescription}
        path={page.slug}
        jsonLd={jsonLd}
      />

      <section className="rl-hero">
        <div className="rl-hero-inner">
          <div>
            <nav className="rl-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>{page.title}</span>
            </nav>
            <h1>{page.h1}</h1>
            <p className="rl-hero-lead">{page.heroLead}</p>
            <div className="rl-hero-note">
              <span><i className="fa fa-road" aria-hidden="true"></i>One Way Taxi</span>
              <span><i className="fa fa-phone" aria-hidden="true"></i><a href="tel:+919629954544">+91 96299 54544</a></span>
            </div>
          </div>

          <div id={BOOKING_ID} className="rl-booking-card">
            <BookingForm
              key={`${page.slug}-${selectedDrop}`}
              initialPickup={pickupOverride || page.pickupName}
              initialDrop={selectedDrop}
              initialTripType="One Way"
              initialVehicle={preferredVehicle}
              searchButtonLabel="Get Fare Estimate"
            />
          </div>
        </div>
      </section>

      <section className="rl-strip" aria-label="City taxi information">
        <div className="container">
          <div className="rl-strip-grid">
            <div className="rl-strip-item">
              <span className="rl-strip-label">Pickup city</span>
              <span className="rl-strip-value">{page.name}</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Trip type</span>
              <span className="rl-strip-value">One Way Taxi</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Priority routes</span>
              <span className="rl-strip-value">{page.priorityRoutes.length} listed</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Support</span>
              <span className="rl-strip-value">+91 96299 54544</span>
            </div>
          </div>
        </div>
      </section>

      <FeaturedRoutePromotion
        origin={page.name}
        destination={page.priorityRoutes[0]?.destination || page.name}
        onGetFareEstimate={({ origin, destination, vehicleType }) => {
          setPickupOverride(origin);
          setSelectedDrop(destination);
          setPreferredVehicle(vehicleType);
          scrollToBooking();
        }}
      />

      <section className="rl-section">
        <div className="container">
          <h2>{page.title} Booking</h2>
          <div className="rl-prose">
            {page.intro.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Priority Routes from {page.name}</h2>
          <p className="rl-section-intro">
            Cards link only when a landing page already exists. Other listed trips are booked in the form on this page.
          </p>
          <div className="rl-route-grid">
            {page.priorityRoutes.map((route) => (
              <article key={`${route.origin}-${route.destination}`} className="rl-benefit-card">
                <i className="fa fa-road" aria-hidden="true"></i>
                <h3>{route.label}</h3>
                <p>{route.note}</p>
                <div className="rl-hub-route-actions">
                  {route.path ? (
                    <Link className="rl-select-btn" to={route.path}>Open route page</Link>
                  ) : (
                    <button
                      type="button"
                      className="rl-select-btn"
                      onClick={() => handleBookRoute(route.destination)}
                    >
                      Book on this page
                    </button>
                  )}
                  {route.reversePath ? (
                    <Link to={route.reversePath}>
                      Reverse: {route.destination} to {route.origin}
                    </Link>
                  ) : null}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Choose Your Taxi</h2>
          <p className="rl-section-intro">
            The same Sedan, SUV and Crysta options used across Easy Drop Taxi. Sample totals appear after you enter a drop and request an estimate — this hub does not invent a city fare.
          </p>
          <div className="rl-vehicle-grid">
            {VEHICLES.map((vehicle) => (
              <article key={vehicle.type} className="rl-vehicle-card">
                <div className="rl-vehicle-media">
                  <img
                    src={vehicle.fleetImage || vehicle.image}
                    alt={`${getVehicleDisplayName(vehicle)} taxi available for booking from ${page.name}`}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                <div className="rl-vehicle-body">
                  <h3>{getVehicleDisplayName(vehicle)}</h3>
                  <p>{vehicle.description}</p>
                  <div className="rl-vehicle-meta">
                    <span><i className="fa fa-users" aria-hidden="true"></i>{vehicle.seats}</span>
                    <span><i className="fa fa-tag" aria-hidden="true"></i>₹{vehicle.rate}/km</span>
                  </div>
                  <p>Use Get Fare Estimate after you add a drop from {page.name}.</p>
                  <button
                    type="button"
                    className={`rl-select-btn${preferredVehicle === vehicle.type ? ' is-selected' : ''}`}
                    onClick={() => handleSelectVehicle(vehicle.type)}
                  >
                    {preferredVehicle === vehicle.type ? 'Selected' : 'Select'}
                  </button>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Why Book from {page.name}</h2>
          <div className="rl-benefit-grid">
            {page.benefits.map((item) => (
              <article key={item.title} className="rl-benefit-card">
                <i className="fa fa-check" aria-hidden="true"></i>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Pickup in {page.name}</h2>
          <div className="rl-prose">
            <p>{page.serviceNote}</p>
            <p>
              One-way billing uses a {MIN_ONEWAY_KM} km minimum and ₹{DRIVER_BATA} driver bata, matching every other Easy Drop Taxi estimate. Toll, state permit, parking and waiting time stay extra as shown at review.
            </p>
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>How to Book</h2>
          <div className="rl-step-grid">
            {page.howTo.map((text, index) => (
              <article key={text} className="rl-step-card">
                <span className="rl-step-number">{index + 1}</span>
                <h3>{['Enter trip details', 'Select vehicle and time', 'Confirm the booking'][index]}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="rl-faq">
            {page.faqs.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Related Pages</h2>
          <p className="rl-section-intro">
            {linkedRoutes.length
              ? `Existing route pages from or into ${page.name} are included with the other city taxi hubs.`
              : `Dedicated pair pages from ${page.name} are not published yet. Other city taxi hubs and Home are linked instead.`}
          </p>
          <div className="rl-links">
            {relatedLinks.map((link) => (
              <Link key={link.path} to={link.path}>{link.label}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-cta">
        <div className="container">
          <h2>{page.ctaTitle}</h2>
          <p>{page.ctaBody}</p>
          <button type="button" className="rl-cta-btn" onClick={scrollToBooking}>
            Book Your Taxi
          </button>
        </div>
      </section>
    </main>
  );
};

export default HubLandingPage;
